import React from 'react'
import _ from 'lodash/fp'
import {Button, Icon, setNotification, useNavigate} from '@startlibs/components'
import {ConfirmEditableBoxes, EditableBox, Field, TextInput, validation, WithForm} from '@startlibs/form'
import {postFetcher} from '@startlibs/utils'
import {useToggle} from '@startlibs/core'
import {bearerHeader} from '../reducers'
import {confirmPasswords, emailValidation, required} from '../lib/validation'
import {
  AdditionalActionButtons,
  Card,
  CardHeader,
  FieldRequirements,
  SignInLayout,
  Toggle,
  ToggleContainer
} from '../components/SigninLayout'
import styled from 'styled-components'
import {PasswordInput} from '../components/PasswordInput'
import {SupportMessage} from '../components/SupportMessage'
import {FormattedMessage, useIntl} from "react-intl";

const LockedIcon = styled(Icon)`
  user-select: none;
  position: absolute;
  outline: none;
  bottom: 0;
  font-size: 21px;
  line-height: 3rem;
  right: 0;
  width: 2rem;
  pointer-events: none;
  color: #969696;
`

const DEFAULT_FORM = {rememberMe: false}

export const AccountSettings = ({userToggle, system}) => {
  const user = userToggle.isOpen
  const intl = useIntl()
  const accountSettings = useToggle()
  const visiblePassword = useToggle()
  const navigate = useNavigate()
  const logout = () => {
    userToggle.openWith({})
    navigate(`/?systemId=${system.systemId}&username=${user.username}`,{state:{loginKey:Date.now(),expiredSession:true}})
  }

  const onFailure = validation.responseFailure((response) => {
    if (response.status === 401) {
      logout()
      return true
    } else {
      const incorretPassword = intl.formatMessage({
        defaultMessage:'Incorrect password',
        description:'Account settings error, incorrect password'
      })
      const {title, type, message} = response
      return (title === 'Incorrect password' && {
          password: [incorretPassword + '.'],
          currentPassword: [incorretPassword + '.']
        }) ||
        (title === 'passwordCannotBeEqualToThePreviousPasswords' && {
          newPassword: [
            intl.formatMessage({
                defaultMessage: `Your new password must be different from your previous {pwdInHistory} passwords.`,
                description: 'Account settings error, new password must be different from previous'
              }, {pwdInHistory: system.systemAuthPolicy.pwdInHistory}
            )]
        }) ||
        (type === 'https://pas.purview.net/problem/invalid-password' && {newPassword: true}) ||
        (message === 'error.canNotChange2FA' && {
          enabled: [intl.formatMessage({
            description: 'System has 2FA enabled for all users',
            defaultMessage: '2FA is enabled for all users by the administrator'
          })]
        })
    }
  })


  const emailPreValidation = validation.buildValidation({
    password: [required(intl)],
    email: [required(intl), emailValidation(intl)]
  })

  const using2FAValidation = validation.buildValidation({
    password: [required(intl)]
  })

  const passwordPreValidation = validation.buildValidation({
    currentPassword: [required(intl)],
    newPassword: [required(intl), confirmPasswords(intl,'confirmNewPassword')]
  })

  const namePreValidation = validation.buildValidation({
    firstName: [required(intl)],
    lastName: [required(intl)]
  })
  return (
    <ConfirmEditableBoxes>
    <SignInLayout hideSupportMessage system={system}>
      <Card>
        <CardHeader>
          <h1><FormattedMessage
            defaultMessage="User authentication settings"
            description="Account settings page title"
          /></h1>
        </CardHeader>
        <Field label={intl.formatMessage({
          defaultMessage: "Name",
          description: "Account settings name field label"
        })}>
          <WithForm
            skipDom
            action={({firstName,lastName,email}) => postFetcher("/pasapi/account", {firstName,lastName,email},bearerHeader(user.id_token))}
            onSuccess={(values) => userToggle.openWith(_.assign(_,values))}
            values={userToggle.isOpen}
            preValidation={namePreValidation} onFailure={onFailure}>
            {() =>
              <EditableBox
                path={[]}
                view={(values) => values.firstName + ' ' + values.lastName}
                edit={() =>
                  <>
                    <TextInput
                      mandatory
                      name="firstName"
                      path="firstName"
                      label={intl.formatMessage({
                        defaultMessage: "First name",
                        description: "Account settings first name input label"
                      })}
                    />
                    <TextInput
                      mandatory
                      name="lastName"
                      path="lastName"
                      label={intl.formatMessage({
                        defaultMessage: "Last name",
                        description: "Account settings last name input label"
                      })}
                    />
                  </>
                }
              />
            }
          </WithForm>
        </Field>
        <Field label={intl.formatMessage({
          defaultMessage: "Email",
          description: "Account settings email field label"
        })}>
          <WithForm
            skipDom
            action={({email,password}) => postFetcher(`/pasapi/account/email-change/init?locale=${intl.locale}`, {email,password},bearerHeader(user.id_token))}
            onSuccess={({email}) => userToggle.openWith(_.set('newEmail',email))}
            preValidation={emailPreValidation}
            onFailure={onFailure}>
            {() =>
              <EditableBox
                path={[]}
                edit={() =>
                  <>
                    <TextInput
                      mandatory
                      path="email"
                      label={intl.formatMessage({
                        defaultMessage: "New email",
                        description: "Account settings new email input label"
                      })}
                      descText={<span><FormattedMessage
                        defaultMessage="Your current email is: <b>{email}</b>"
                        description="Account settings current email input description"
                        values={{
                          email: user.email,
                          b: (chunks) => <b>{chunks}</b>
                        }}
                      /></span>}
                    />
                    <PasswordInput
                      path="password"
                      mandatory
                      label={intl.formatMessage({
                        defaultMessage: "Account password",
                        description: "Account settings password input label"
                      })}
                      descText={intl.formatMessage({
                        defaultMessage: "For security reasons you need to enter your account password to change the email address.",
                        description: "Account settings password input description"
                      })}
                    />
                    <span className="field-description"><FormattedMessage
                      defaultMessage="In order for this change to take effect, you must confirm your email from a link that will be sent to the new registered address."
                      description="Account settings email change description"
                    /></span>
                  </>
                }
                view={() => user.email}
              />
            }
          </WithForm>
          {user.newEmail &&
            <span className="post-field-description"><FormattedMessage
              defaultMessage="Email change requested to <b>{newEmail}</b>. Click on the link sent to this address to effect the change."
              description="Account settings email change requested description"
              values={{
                newEmail: user.newEmail,
                b: (chunks) => <b>{chunks}</b>
              }}
            /></span>}
        </Field>
        <Field label={intl.formatMessage({
          defaultMessage: "Username",
          description: "Account settings username field label"
        })}>
          <div className="editable-box collapsed locked">
            {user.username}
            <LockedIcon icon="lock"/>
          </div>
        </Field>
        <Field label={intl.formatMessage({
          defaultMessage: "Password",
          description: "Account settings password field label"
        })}>
          <WithForm
            skipDom
            action={(values) => postFetcher(`/pasapi/account/change-password?locale=${intl.locale}`, values,bearerHeader(user.id_token))}
            preValidation={passwordPreValidation}
            onSucess={() => setNotification(intl.formatMessage({
              defaultMessage:"Password changed successfully",
              description:"Account settings password change success message"
            }))}
            onFailure={onFailure}>
            {(form) =>
              <EditableBox
                view={() => <i><FormattedMessage
                  defaultMessage="Click to change your password"
                  description="Account settings change password button"
                /></i>}
                edit={() =>
                  <>
                    <PasswordInput
                      mandatory
                      path="currentPassword"
                      label={intl.formatMessage({
                        defaultMessage: "Current password",
                        description: "Account settings current password input label"
                      })}
                    />
                    <PasswordInput
                      mandatory
                      path="newPassword"
                      syncVisible={visiblePassword}
                      label={intl.formatMessage({
                        defaultMessage: "New password",
                        description: "Account settings new password input label"
                      })}
                    />
                    <PasswordInput
                      mandatory
                      path="confirmNewPassword"
                      syncVisible={visiblePassword}
                      label={intl.formatMessage({
                        defaultMessage: "Confirm new password",
                        description: "Account settings confirm new password input label"
                      })}
                    />
                    <FieldRequirements hasErrors={form.hasErrors('newPassword')}>
                      <FormattedMessage
                        defaultMessage={`Your new password must be <b>{pwdMinimumLength} or more characters</b>,
                        containing at least <b>one uppercase letter</b>, one <b>number</b> and one <b>special
                        character</b>.`}
                        description="Account settings new password requirements"
                        values={{
                          pwdMinimumLength: system.systemAuthPolicy.pwdMinimumLength,
                          b: (chunks) => <b>{chunks}</b>
                        }}
                      />
                    </FieldRequirements>
                  </>
                }
              />
            }
          </WithForm>
        </Field>
        <Field label={intl.formatMessage({
          defaultMessage: "Two-Factor Authentication",
          description: "Account settings 2FA field label"
        })}>
          {
            system.using2FA
              ? <div className="editable-box collapsed locked">
                <i><FormattedMessage
                  defaultMessage="Enabled for all users by the administrator"
                  description="Account settings 2FA enabled description"
                /></i>
                <LockedIcon icon="lock"/>
              </div>
              : <WithForm
                skipDom
              onSuccess={({enabled}) => userToggle.openWith(_.set('using2FA',enabled))}
                action={({enabled,password}) => postFetcher(`/pasapi/account/change-2fa?locale=${intl.locale}`, {enabled,password},bearerHeader(user.id_token))}
                values={{enabled: user.using2FA}}
                preValidation={using2FAValidation}
                onFailure={onFailure}>
                {(form) =>
                  <EditableBox
                    path={[]}
                    edit={(v) =>
                      <>
                        <p><FormattedMessage
                          defaultMessage="After you log in with your password, you’ll be asked for a temporary code sent to your email."
                          description="Account settings 2FA description"
                        /></p>
                        <ToggleContainer onClick={() => form.setValue('enabled', !form.getValue('enabled'))}>
                          <><b><FormattedMessage
                            defaultMessage="Enabled"
                            description="Account settings 2FA enabled label"
                          /></b><Toggle checked={form.getValue("enabled")}/></>
                        </ToggleContainer>
                        <PasswordInput
                          path="password"
                          mandatory
                          label={intl.formatMessage({
                            defaultMessage: "Account password",
                            description: "Account settings password input label"
                          })}
                          descText={intl.formatMessage({
                            defaultMessage: "For security reasons you need to enter your account password to change this option.",
                            description: "Account settings password input description"
                          })}
                        />
                      </>
                    }
                    view={() =>
                      (user.using2FA && 'Enabled') ||
                      <i><FormattedMessage
                        defaultMessage="Click to set up extra security with two-factor authentication"
                        description="Account settings 2FA setup button"
                      /></i>
                    }
                  />
                }
              </WithForm>
          }

        </Field>
      </Card>
      <SupportMessage system={system}/>
      <AdditionalActionButtons>
        {/*<Button outline small onClick={logout}>Sign out</Button>*/}
        <Button.a small highlight href={system.systemWebHooks.systemAuthWebHook + user.id_token + "&locale="+intl.locale}>
          {system.labels.systemGoTo || <FormattedMessage
            defaultMessage="Go to case list"
            description="Account settings go to case list button"
          />}
        </Button.a>
      </AdditionalActionButtons>
    </SignInLayout></ConfirmEditableBoxes>
  )
}